import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Compass, Lightbulb, Camera, Scissors, TrendingUp, ChevronRight, ChevronDown } from "lucide-react";
import { PROCESS } from "../data/content";
import { Reveal, Overline } from "./Reveal";

const ICONS = [Compass, Lightbulb, Camera, Scissors, TrendingUp];

export default function Process() {
  const timelineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start 80%", "end 60%"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const arrowY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="methode"
      data-testid="process-section"
      className="section-round bg-[#F2F6FB] px-6 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <Reveal className="mb-16 text-center">
          <Overline>Notre collaboration</Overline>
          <h2 className="font-head mx-auto mt-4 max-w-3xl text-4xl font-extrabold text-[#0B1220] sm:text-5xl">
            Comment se déroule notre <span className="text-[#3B82F6]">collaboration</span>.
          </h2>
        </Reveal>

        {/* Desktop / tablet grid */}
        <div className="hidden gap-6 sm:grid sm:grid-cols-2 lg:grid-cols-5">
          {PROCESS.map((step, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`process-step-${i}`}
                className="card-dark group relative rounded-3xl p-6"
              >
                <span className="font-head pointer-events-none absolute right-3 top-2 text-6xl font-extrabold text-stroke">
                  {step.n}
                </span>

                {i < PROCESS.length - 1 && (
                  <div className="pointer-events-none absolute left-full top-[46px] z-20 hidden h-[2px] w-6 -translate-y-1/2 lg:block">
                    <span className="absolute inset-0 rounded-full bg-[#0B1220]/[0.06]" />
                    <span className="absolute inset-y-0 left-0 w-0 rounded-full bg-[#3B82F6] shadow-[0_0_10px_rgba(59,130,246,0.7)] transition-all duration-[600ms] ease-out group-hover:w-full" />
                    <ChevronRight
                      size={14}
                      className="absolute -right-1.5 top-1/2 -translate-y-1/2 text-[#0B1220]/25 transition-all duration-500 group-hover:right-[-7px] group-hover:text-[#3B82F6]"
                    />
                  </div>
                )}

                <span className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-[#2563EB]/15 text-[#3B82F6] transition-colors duration-300 group-hover:bg-[#2563EB] group-hover:text-white">
                  <Icon size={20} />
                </span>
                <h3 className="font-head relative mt-6 text-lg font-bold text-[#0B1220]">
                  {step.title}
                </h3>
                <p className="relative mt-3 text-sm leading-relaxed text-[#5B6B85]">{step.text}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Mobile vertical timeline */}
        <div ref={timelineRef} data-testid="process-timeline-mobile" className="relative sm:hidden">
          {/* Rail (base) — center at X=28px */}
          <span
            aria-hidden
            className="pointer-events-none absolute left-[27px] top-2 bottom-2 w-[2px] rounded-full bg-[#0B1220]/[0.08]"
          />
          {/* Rail (progress fill) */}
          <motion.span
            aria-hidden
            style={{ height: lineHeight }}
            className="pointer-events-none absolute left-[27px] top-2 w-[2px] rounded-full bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] shadow-[0_0_12px_rgba(59,130,246,0.55)]"
          />
          {/* Traveling arrow — center at X=28px (w-5 = 20px, left = 18px) */}
          <motion.span
            aria-hidden
            style={{ top: arrowY }}
            className="pointer-events-none absolute left-[18px] z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-[#2563EB] text-white shadow-[0_0_12px_rgba(59,130,246,0.75)]"
          >
            <ChevronDown size={12} strokeWidth={3} />
          </motion.span>

          <ul className="space-y-6 pl-[3.75rem]">
            {PROCESS.map((step, i) => {
              const Icon = ICONS[i];
              return (
                <motion.li
                  key={step.n}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                  transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`process-step-mobile-${i}`}
                  className="card-dark relative rounded-2xl p-5"
                >
                  {/* Node dot — center at X=28px (card left = 60px, so dot needs left = -42px) */}
                  <span
                    aria-hidden
                    className="absolute -left-[42px] top-6 flex h-5 w-5 items-center justify-center rounded-full bg-white ring-2 ring-[#3B82F6] shadow-[0_0_0_4px_rgba(59,130,246,0.12)]"
                  >
                    <span className="h-2 w-2 rounded-full bg-[#3B82F6]" />
                  </span>

                  <span className="font-head pointer-events-none absolute right-3 top-1 text-5xl font-extrabold text-stroke">
                    {step.n}
                  </span>

                  <span className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-[#2563EB]/15 text-[#3B82F6]">
                    <Icon size={18} />
                  </span>
                  <h3 className="font-head relative mt-4 text-base font-bold text-[#0B1220]">
                    {step.title}
                  </h3>
                  <p className="relative mt-2 text-sm leading-relaxed text-[#5B6B85]">
                    {step.text}
                  </p>
                </motion.li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
